<template>
  <div class="row" style="background-color: #E3E8EE">
    <div class="col-xl-5">
      <div class="card mb-1" v-permission="'change_customer'">
        <div class="card-body d-flex align-items-center">
          <b-form-group class="mb-0">
            <b-form-checkbox :checked="customer.email_opt_out" size="lg" @change="onEmailOptOutChange" switch>
              <h5 class="d-inline-block mb-0">
                {{ $t('Email Opt Out') }}
              </h5>
              <span>
                <b-tooltip :target="`optout-question-mark`" :title="$t('If ticked, following customer will not receive emails from Artisio.')"/>
                <font-awesome-icon id="optout-question-mark" icon="question-circle" class="icon ml-2"/>
              </span>
            </b-form-checkbox>
          </b-form-group>
        </div>
      </div>
      <email-notification-setup :loading="loading"></email-notification-setup>
    </div>
    <div class="col-xl-7">
      <div class="card mb-1" v-permission="'change_customer'">
        <div class="card-body d-flex align-items-center">
          <b-form-group class="mb-0">
            <b-form-checkbox v-model="customer.optin" size="lg" @change="onOptinChange" switch :disabled="disableCheckboxes">
              <h5 class="d-inline-block mb-0">
                {{ $t('Marketing Newsletter Optin') }}
              </h5>
              <span>
                <b-tooltip :target="`optin-question-mark`" :title="$t('If ticked, following customer will receive marketing newsletter from Artisio.')"/>
                <font-awesome-icon id="optin-question-mark" icon="question-circle" class="icon ml-2"/>
              </span>
            </b-form-checkbox>
          </b-form-group>
        </div>
      </div>
      <div class="card mb-1" v-permission="'change_customer'">
        <div class="card-body d-flex align-items-center">
          <b-form-group class="mb-0">
            <b-form-checkbox :checked="customer.default_audience" size="lg" @change="onDefaultAudienceChange" switch>
              <h5 class="d-inline-block mb-0">
                {{ $t('Default Audience') }}
              </h5>
              <span>
                <b-tooltip :target="`default-audience-question-mark`" :title="$t('If ticked, following customer will receive marketing newsletter for all interest categories from Artisio.')"/>
                <font-awesome-icon id="default-audience-question-mark" icon="question-circle" class="icon ml-2"/>
              </span>
            </b-form-checkbox>
          </b-form-group>
        </div>
      </div>
      <div class="card col-xl-12 mt-sm-3 mt-xl-0 mb-1">
        <div class="card-header">
          <h5 class="m-0">{{ $t('Interest Categories') }}</h5>
        </div>
        <div class="card-body">
          <spinner :show="loading"/>
          <div v-if="!loading" class="row">
            <div class="col-sm-6 col-md-4 col-lg-3 pb-3" v-for="department in departments" :key="department.uuid">
              <b-form-checkbox :disabled="!$hasPermission('change_customer') || disableCheckboxes"
                               v-model="department.checked" @change="onCheckboxChange" switch>
              <span class="d-inline-block mb-0">
                {{ department.name | masterLanguage }}
              </span>
              </b-form-checkbox>
            </div>
          </div>
        </div>
      </div>
      <div class="card col-xl-12 mt-sm-3 mt-xl-0">
        <div class="card-header">
          <h5 class="m-0">{{ $t('Notification Opt-in/out') }}</h5>
        </div>
        <div class="card-body">
          <templates-unsubscribe />
        </div>
      </div>
    </div>
    <templates-to-emails-modal></templates-to-emails-modal>
  </div>
</template>

<script>
  import {createNamespacedHelpers} from "vuex";
  import Spinner from "@/core/components/common/Spinner";
  import CustomerService from "@/modules/customer/CustomerService";
  import _ from 'lodash';
  import NoDataAvailable from "../../../../core/components/common/NoDataAvailable";
  import TemplatesToEmailsModal from "./TemplatesToEmailsModal";
  import EmailNotificationSetup from "./EmailNotificationSetup";
  import TemplatesUnsubscribe from "@/modules/customer/view/optin/TemplatesUnsubscribe.vue";

  const {mapState, mapActions} = createNamespacedHelpers('customer/customerView');
  const {mapState: departmentMapState} = createNamespacedHelpers('setup/department');

  export default {
    name: "Optin",
    components: {TemplatesUnsubscribe, EmailNotificationSetup, Spinner, NoDataAvailable, TemplatesToEmailsModal},
    data() {
      return {
        loading: false,
        departments: [],
        disableCheckboxes: false,
      }
    },
    computed: {
      ...mapState({
        customer: state => state.customer,
      }),
      ...departmentMapState({
        stateDepartments: state => state.departments.rows
      }),
    },
    methods: {
      ...mapActions(['setTemplatesToEmailsTableData']),
      initDepartments() {
        if (this.departments.length > 0) {
          return;
        }

        this.departments = this.stateDepartments.map(department => {
          return {..._.cloneDeep(department), checked: false}
        });
      },
      onCheckboxChange() {
          this.disableCheckboxes = true;
          this.debouncedCheckboxChange();
      },
      debouncedCheckboxChange: _.debounce(async function () {
        let uuids = this.departments.filter(d => d.checked).map(d => d.uuid)
        try {
          let response = await CustomerService.partialUpdateCustomer({
            id: this.customer.uuid, data: {interest_departments_uuids: uuids}
          })
          if (response.success) {
            this.$toast('success', this.$t('Interest categories were successfully saved'));
          } else {
            if (response.body.non_field_errors) {
              this.$errorDialog(response.body.non_field_errors[0])
            }
          }
        } finally {
            this.disableCheckboxes = false;
        }
      }, 500),
      async onEmailOptOutChange(checked) {
        let response = await CustomerService.partialUpdateCustomer({
          id: this.customer.uuid,
          data: {email_opt_out: checked}
        });
        if (response.success) {
          this.customer.email_opt_out = checked;
          this.$toast('success', this.$t('Email Opt Out was saved successfully'));
        }
      },
      async onOptinChange(checked) {
        this.disableCheckboxes = true;
        try {
          let response = await CustomerService.partialUpdateCustomer({
            id: this.customer.uuid,
            data: {optin: checked}
          });
          if (response.success) {
            this.customer.optin = checked;
            this.$toast('success', this.$t('Marketing Newsletter Optin was saved successfully'));
          } else {
            this.customer.optin = !checked;
            if (response.body.non_field_errors) {
              this.$errorDialog(response.body.non_field_errors[0]);
            } else {
              this.$errorDialog(this.$t('failed to save'));
            }
          }
        } finally {
          this.disableCheckboxes = false;
        }
      },
      async onDefaultAudienceChange(checked) {
        let response = await CustomerService.partialUpdateCustomer({
          id: this.customer.uuid,
          data: {default_audience: checked}
        });
        if (response.success) {
          this.customer.default_audience = checked;
          this.$toast('success', this.$t('Default Audience was saved successfully'));
        }
      }
    },
    async beforeMount() {
      this.loading = true;
      let response = await CustomerService.getCustomer(this.customer.uuid, 'interest_departments,emails_to_templates')
      this.loading = false;

      if (response.success) {
        this.initDepartments();
        for (let department of response.body.interest_departments) {
          let index = this.departments.findIndex(d => d.uuid === department.uuid);
          if (index >= 0) {
            this.departments[index].checked = true;
          }
        }
        let templateToEmailsMap = {} // example {template_key: [email1, email2, email3..]
        response.body.emails_to_templates.forEach(item => {
          if (!(item.key in templateToEmailsMap)) {
            templateToEmailsMap[item.key] = []
          }
          templateToEmailsMap[item.key].push(item.email);
        })
        let tableData = []
        Object.entries(templateToEmailsMap).forEach(item => {
          tableData.push({
            "template_type": item[0],
            "emails": item[1]
          })
        })
        await this.setTemplatesToEmailsTableData(tableData);
      }
    },
    mounted() {
      this.initDepartments()
    }
  }
</script>

<style scoped>
</style>
